// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/**
 *
 * <HeaderClickable
 *      iconSrc={icon}
 *      onClick={() => !isMobile && history.push(cartUrl ?? "")}
 *      iconProps={{ type: "miniCart" }}
 *      clickableProps={{ "data-test-selector": "cartLink" }}
 *      titleProps={{
 *          "data-test-selector": totalCountDisplay ? "cartLinkQuantity" : "cartLinkSubTotal",
 *      }}
 *      >
 *          <>
 *              {`${translate("Top Message")}`}
 *              <Typography className="sub-title">{`${translate("Bottom Message")}`}</Typography>
 *          </>
 *      </HeaderClickable>
 */

import { CustomAppState } from "@electrowire/App/Store/Reducers";
import { color, ellipsis, hideElement, xClassNames, xScreen } from "@gcg/XDL/Utilities";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Clickable, { ClickablePresentationProps, ClickableProps } from "@insite/mobius/Clickable/Clickable";
import Icon, { IconPresentationProps, IconProps, IconWrapper } from "@insite/mobius/Icon";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, MouseEventHandler } from "react";
import { css } from "styled-components";

interface HeaderClickableProps {
    onClick?: MouseEventHandler<any>;
    iconProps?: IconProps;
    titleProps?: TypographyProps;
    isPopupShown?: boolean;
    numberOfCartItems?: number;
}

type Props = HeaderClickableProps & ClickableProps;

interface HeaderClickableStyle {
    clickable?: ClickablePresentationProps;
    icon?: IconPresentationProps;
    title?: TypographyProps;
    cartItem?: {
        wrapper?: InjectableCss;
        text?: TypographyProps;
    };
}

const styles: HeaderClickableStyle = {
    clickable: {
        css: css`
            &:focus {
                outline: none;
            }
            &.mobile-mode {
                ${TypographyStyle} {
                    display: none;
                }
            }
            column-gap: 8px;
        `,
    },
    icon: {
        color: "common.ui7",
        css: css`
            ${xScreen("gt", "md")} {
                &.cart-items {
                    display: none;
                }
            }
            ${xScreen("lte", "md")} {
                &.cart-items {
                    position: relative;
                    &:before {
                        content: "";
                        height: 20px;
                        min-width: 20px;
                        background-color: ${color("primary")};
                        border-radius: 50%;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        position: absolute;
                        top: -10px;
                        left: 70%;
                        transform: translateX(-70%);
                    }
                    &:after {
                        color: white;
                        font-size: 11px;
                        position: absolute;
                        top: -8px;
                        left: 60%;
                        transform: translateX(-60%);
                    }

                    ${({ numberOfCartItems }: any) => css`
                        &:after {
                            content: "${numberOfCartItems}";
                        }
                    `}
                }
            }
        `,
    },
    title: {
        size: 14,
        weight: 600,
        color: "text",
        ellipsis: true,
        transform: "capitalize",
        css: css`
            letter-spacing: 0;
            line-height: 18px;
        `,
    },
    cartItem: {
        wrapper: {
            css: css`
                height: 25px;
                min-width: 25px;
                margin-right: -4px;
                background-color: ${color("primary")};
                border-radius: 50%;
                display: flex;
                align-items: center;
                justify-content: center;
                ${xScreen("lte", "md")} {
                    display: none;
                }
            `,
        },
        text: {
            size: 12,
            lineHeight: "15px",
            weight: "bold",
            color: "common.white",
            css: css`
                margin-left: -1px;
            `,
        },
    },
};

const HeaderClickable: FC<Props> = ({
    numberOfCartItems,
    isPopupShown,
    iconProps,
    titleProps,
    children,
    ...otherProps
}) => {
    const { isTabletScreen } = useAppSelector(state => ({
        isTabletScreen: (state as CustomAppState).xCommon.isTabletScreen,
    }));

    const isCartItems = !!numberOfCartItems && numberOfCartItems > 0;
    const classNames = xClassNames.append([
        isPopupShown ? "popup-shown" : "",
        isTabletScreen ? "mobile-mode" : "",
        isCartItems ? "cart-items" : "",
    ]);
    return (
        <Clickable {...styles.clickable} {...otherProps} className={classNames}>
            {isCartItems && (
                <StyledWrapper className={classNames} {...styles.cartItem?.wrapper}>
                    <Typography {...styles.cartItem?.text} {...titleProps}>
                        {numberOfCartItems}
                    </Typography>
                </StyledWrapper>
            )}
            <Icon
                {...styles.icon}
                size={isTabletScreen ? 28 : 14}
                {...iconProps}
                {...{ numberOfCartItems }}
                className={classNames}
            />
            <Typography {...styles.title} {...titleProps}>
                {children}
            </Typography>
        </Clickable>
    );
};

export default HeaderClickable;
